import {
    type ImageSourcePropType,
    type ViewStyle,
    StyleSheet
} from "react-native";
import type {
    AspectRatioStateHeightConstantType,
    AspectRatioStatusConstantType,
    AspectRatioRadiusConstantType,
    AspectRatioRatioConstantType,
    AspectRatioDynamicStyleType,
    AspectRatioPreset,
    AspectRatioStatus
} from "./type";
import type {
    Mutable
} from "../../types";
import {
    concentric
} from "../../helpers/theme/concentric";
import {
    getCornerStyle
} from "../../helpers/theme/corner";

export const ASPECT_RATIO_PRESETS: Record<AspectRatioPreset, number> = {
    "16:9": 16 / 9,
    "21:9": 21 / 9,
    "3:4": 3 / 4,
    "4:3": 4 / 3,
    "1:1": 1
};

export const ASPECT_RATIO_DEFAULT: AspectRatioPreset = "16:9";

export const getAspectRatioValue = ({
    ratio
}: AspectRatioRatioConstantType): number => {
    const currentRatio = typeof ratio === "number" ? ratio : ASPECT_RATIO_PRESETS[ratio];

    if(!currentRatio || !Number.isFinite(currentRatio) || currentRatio <= 0) {
        return ASPECT_RATIO_PRESETS[ASPECT_RATIO_DEFAULT];
    }

    return currentRatio;
};

export const getAspectRatioRadius = ({
    radiusRole,
    cardInset,
    radiuses,
    borders
}: AspectRatioRadiusConstantType): number => {
    if(radiusRole === "form") {
        return radiuses.form;
    }

    if(radiusRole === "flush") {
        return Math.max(0, radiuses.surface - borders.line);
    }

    if(radiusRole === "auto" && cardInset !== undefined) {
        return concentric({
            inset: cardInset + borders.line,
            outer: radiuses.surface,
            role: radiuses.form
        });
    }

    return radiuses.surface;
};

export const getAspectRatioSourceKey = ({
    source
}: {
    source?: ImageSourcePropType;
}): string => {
    if(!source) {
        return "";
    }

    if(typeof source === "number") {
        return `${source}`;
    }

    if(Array.isArray(source)) {
        return source.map((item) => item.uri ?? "").join("|");
    }

    return source.uri ?? "";
};

export const getAspectRatioStatus = ({
    isRestricted,
    hasContent,
    isLoading,
    isFailed
}: AspectRatioStatusConstantType): AspectRatioStatus => {
    if(isRestricted) {
        return "restricted";
    }

    if(isLoading) {
        return "loading";
    }

    if(isFailed) {
        return "failed";
    }

    return hasContent ? "ready" : "empty";
};

export const getAspectRatioStateHeight = ({
    controlHeights,
    hasDescription,
    typography,
    hasAction,
    iconSizes,
    spaces
}: AspectRatioStateHeightConstantType): number => {
    let height = spaces.spacingMd * 2 + iconSizes.large * 2 + spaces.spacingSm + typography.labelMediumSize.lineHeight;

    if(hasDescription) {
        height += spaces.spacingXxs + typography.bodySmallSize.lineHeight * 2;
    }

    if(hasAction) {
        height += spaces.spacingSm + controlHeights.small;
    }

    return height;
};

const stylesheet = StyleSheet.create({
    fill: {
        ...StyleSheet.absoluteFill
    },
    container: {
        overflow: "hidden",
        width: "100%"
    }
});

export const useStyles = ({
    borderRadius,
    shapeConfigs,
    radiusRole,
    flushEdge,
    colors,
    ratio
}: Omit<AspectRatioDynamicStyleType, "spaces" | "objectFit">) => {
    const {
        borderRadius: cornerRadius,
        ...cornerShape
    } = getCornerStyle({
        radius: borderRadius,
        shapeConfigs
    });

    const styles = {
        container: {
            backgroundColor: colors.content.container.subtle,
            borderRadius: cornerRadius,
            aspectRatio: ratio,
            ...cornerShape
        } as Mutable<ViewStyle>
    };

    if(radiusRole === "flush") {
        delete styles.container.borderRadius;

        if(flushEdge !== "bottom") {
            Object.assign(styles.container, {
                borderTopRightRadius: cornerRadius,
                borderTopLeftRadius: cornerRadius
            });
        }

        if(flushEdge !== "top") {
            Object.assign(styles.container, {
                borderBottomRightRadius: cornerRadius,
                borderBottomLeftRadius: cornerRadius
            });
        }
    }

    return styles;
};

export default stylesheet;
